import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";

import { Button } from "@/components/ui/Button";
import { extractApiError } from "@/lib/api-error";

import { forgotPassword, verifyResetCode, confirmResetPassword } from "../api/authApi";

const emailSchema = z.object({
    email: z.string().email("Enter a valid admin email")
});

const codeSchema = z.object({
    code: z.string().length(6, "Code must be 6 digits").regex(/^\d+$/, "Code must contain only numbers")
});

const resetSchema = z
    .object({
        password: z.string().min(8, "Password must be at least 8 characters"),
        confirmPassword: z.string()
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: "Passwords do not match",
        path: ["confirmPassword"]
    });

type EmailFormValues = z.infer<typeof emailSchema>;
type CodeFormValues = z.infer<typeof codeSchema>;
type ResetFormValues = z.infer<typeof resetSchema>;

export const ForgotPasswordForm = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState<"email" | "verify" | "reset">("email");
    const [email, setEmail] = useState("");
    const [verificationToken, setVerificationToken] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const emailForm = useForm<EmailFormValues>({
        resolver: zodResolver(emailSchema),
        defaultValues: {
            email: ""
        }
    });

    const codeForm = useForm<CodeFormValues>({
        resolver: zodResolver(codeSchema),
        defaultValues: {
            code: ""
        }
    });

    const resetForm = useForm<ResetFormValues>({
        resolver: zodResolver(resetSchema),
        defaultValues: {
            password: "",
            confirmPassword: ""
        }
    });

    const onEmailSubmit = async ({ email }: EmailFormValues) => {
        setIsLoading(true);
        try {
            await forgotPassword({ email });
            setEmail(email);
            setStep("verify");
        } catch (error) {
            emailForm.setError("root", { type: "server", message: extractApiError(error) });
        } finally {
            setIsLoading(false);
        }
    };

    const onCodeVerify = async ({ code }: CodeFormValues) => {
        setIsLoading(true);
        try {
            const response = await verifyResetCode({ email, code });
            if (response.data?.verificationToken) {
                setVerificationToken(response.data.verificationToken);
                setStep("reset");
            }
        } catch (error) {
            codeForm.setError("root", { type: "server", message: extractApiError(error) });
        } finally {
            setIsLoading(false);
        }
    };

    const onResetSubmit = async ({ password }: ResetFormValues) => {
        setIsLoading(true);
        try {
            await confirmResetPassword({ verificationToken, newPassword: password });
            navigate("/auth/login", { state: { message: "Password reset successful. Please login." } });
        } catch (error) {
            resetForm.setError("root", { type: "server", message: extractApiError(error) });
        } finally {
            setIsLoading(false);
        }
    };

    if (step === "verify") {
        return (
            <div className="space-y-8">
                <header className="space-y-2">
                    <p className="text-sm text-slate-500 dark:text-slate-300">
                        Enter the 6-digit code sent to <strong>{email}</strong> to verify your identity.
                    </p>
                </header>

                <div className="border border-slate-200 bg-white/80 p-8 shadow-xl shadow-slate-900/5 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/80">
                    <form className="space-y-6" onSubmit={codeForm.handleSubmit(onCodeVerify)} noValidate>
                        <div className="space-y-1.5">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                                Verification code
                            </label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                autoComplete="one-time-code"
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-center text-2xl font-mono tracking-widest text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100 dark:focus:border-primary-500"
                                placeholder="000000"
                                {...codeForm.register("code")}
                            />
                            {codeForm.formState.errors.code ? (
                                <p className="text-xs font-medium text-rose-500">
                                    {codeForm.formState.errors.code.message}
                                </p>
                            ) : null}
                        </div>

                        {codeForm.formState.errors.root ? (
                            <p className="text-sm font-medium text-rose-500">
                                {codeForm.formState.errors.root.message}
                            </p>
                        ) : null}

                        <Button type="submit" className="w-full rounded-full" disabled={isLoading}>
                            {isLoading ? "Verifying..." : "Verify code"}
                        </Button>
                    </form>
                </div>

                <div className="flex items-center justify-between text-sm">
                    <button
                        type="button"
                        onClick={() => setStep("email")}
                        className="font-medium text-primary-600 transition hover:text-primary-500"
                    >
                        ← Change email
                    </button>
                    <button
                        type="button"
                        onClick={async () => {
                            setIsLoading(true);
                            try {
                                await forgotPassword({ email });
                            } catch (error) {
                                codeForm.setError("root", { type: "server", message: extractApiError(error) });
                            } finally {
                                setIsLoading(false);
                            }
                        }}
                        className="font-medium text-primary-600 transition hover:text-primary-500"
                        disabled={isLoading}
                    >
                        Resend code
                    </button>
                </div>

                <footer className="text-xs text-slate-400 dark:text-slate-500">
                    Need help? Email{" "}
                    <a
                        href="mailto:support@ridetofive.com"
                        className="font-medium text-primary-600 hover:text-primary-500"
                    >
                        support@ridetofive.com
                    </a>
                    .
                </footer>
            </div>
        );
    }

    if (step === "reset") {
        return (
            <div className="space-y-8">
                <header className="space-y-2">
                    <p className="text-sm text-slate-500 dark:text-slate-300">
                        Create a new password for your account.
                    </p>
                </header>

                <div className="border border-slate-200 bg-white/80 p-8 shadow-xl shadow-slate-900/5 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/80">
                    <form className="space-y-6" onSubmit={resetForm.handleSubmit(onResetSubmit)} noValidate>

                        <div className="space-y-1.5">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                                New password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="new-password"
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-12 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100 dark:focus:border-primary-500"
                                    placeholder="Enter new password"
                                    {...resetForm.register("password")}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? (
                                        <EyeSlashIcon className="h-5 w-5" />
                                    ) : (
                                        <EyeIcon className="h-5 w-5" />
                                    )}
                                </button>
                            </div>
                            {resetForm.formState.errors.password ? (
                                <p className="text-xs font-medium text-rose-500">
                                    {resetForm.formState.errors.password.message}
                                </p>
                            ) : null}
                        </div>

                        <div className="space-y-1.5">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                                Confirm password
                            </label>
                            <div className="relative">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    autoComplete="new-password"
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-12 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100 dark:focus:border-primary-500"
                                    placeholder="Confirm new password"
                                    {...resetForm.register("confirmPassword")}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                >
                                    {showConfirmPassword ? (
                                        <EyeSlashIcon className="h-5 w-5" />
                                    ) : (
                                        <EyeIcon className="h-5 w-5" />
                                    )}
                                </button>
                            </div>
                            {resetForm.formState.errors.confirmPassword ? (
                                <p className="text-xs font-medium text-rose-500">
                                    {resetForm.formState.errors.confirmPassword.message}
                                </p>
                            ) : null}
                        </div>

                        {resetForm.formState.errors.root ? (
                            <p className="text-sm font-medium text-rose-500">
                                {resetForm.formState.errors.root.message}
                            </p>
                        ) : null}

                        <Button type="submit" className="w-full rounded-full" disabled={isLoading}>
                            {isLoading ? "Resetting..." : "Reset password"}
                        </Button>
                    </form>
                </div>

                <div className="text-center text-sm">
                    <button
                        type="button"
                        onClick={() => setStep("verify")}
                        className="font-medium text-primary-600 transition hover:text-primary-500"
                    >
                        ← Back to verification
                    </button>
                </div>

                <footer className="text-xs text-slate-400 dark:text-slate-500">
                    Need help? Email{" "}
                    <a
                        href="mailto:support@ridetofive.com"
                        className="font-medium text-primary-600 hover:text-primary-500"
                    >
                        support@ridetofive.com
                    </a>
                    .
                </footer>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <header className="space-y-2">
                <p className="text-sm text-slate-500 dark:text-slate-300">
                    Enter your admin email and we'll send you a 6-digit code to reset your password.
                </p>
            </header>

            <div className="border border-slate-200 bg-white/80 p-8 shadow-xl shadow-slate-900/5 backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/80">
                <form className="space-y-6" onSubmit={emailForm.handleSubmit(onEmailSubmit)} noValidate>
                    <div className="space-y-1.5">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                            Admin email
                        </label>
                        <input
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100 dark:focus:border-primary-500"
                            placeholder="you@ridetofive.com"
                            {...emailForm.register("email")}
                        />
                        {emailForm.formState.errors.email ? (
                            <p className="text-xs font-medium text-rose-500">
                                {emailForm.formState.errors.email.message}
                            </p>
                        ) : null}
                    </div>

                    {emailForm.formState.errors.root ? (
                        <p className="text-sm font-medium text-rose-500">
                            {emailForm.formState.errors.root.message}
                        </p>
                    ) : null}

                    <Button type="submit" className="w-full rounded-full" disabled={isLoading}>
                        {isLoading ? "Sending..." : "Send code"}
                    </Button>
                </form>
            </div>

            <div className="text-center">
                <Link
                    to="/auth/login"
                    className="text-sm font-medium text-primary-600 transition hover:text-primary-500"
                >
                    ← Back to login
                </Link>
            </div>

            <footer className="text-xs text-slate-400 dark:text-slate-500">
                Need help? Email{" "}
                <a
                    href="mailto:support@ridetofive.com"
                    className="font-medium text-primary-600 hover:text-primary-500"
                >
                    support@ridetofive.com
                </a>
                .
            </footer>
        </div>
    );
};
